import React, { useState, useMemo } from 'react';
import { ArrowLeft, Gauge, CheckCircle2, XCircle, AlertCircle, Target, ThumbsUp, ThumbsDown, AlertTriangle } from 'lucide-react';
import { scoreResume } from '../atsScore.js';

const VERDICT_ICONS = {
    APPLY: ThumbsUp,
    APPLY_TAILORED: Target,
    BORDERLINE: AlertTriangle,
    SKIP: ThumbsDown,
    SKIP_REWRITE: ThumbsDown
};

const COLOR_MAP = {
    emerald: { bg: 'bg-emerald-500', text: 'text-emerald-600', border: 'border-emerald-200', soft: 'bg-emerald-50' },
    blue: { bg: 'bg-blue-500', text: 'text-blue-600', border: 'border-blue-200', soft: 'bg-blue-50' },
    amber: { bg: 'bg-amber-500', text: 'text-amber-600', border: 'border-amber-200', soft: 'bg-amber-50' },
    orange: { bg: 'bg-orange-500', text: 'text-orange-600', border: 'border-orange-200', soft: 'bg-orange-50' },
    red: { bg: 'bg-red-500', text: 'text-red-600', border: 'border-red-200', soft: 'bg-red-50' }
};

const ATSScorePanel = ({ resumeData, onBack }) => {
    const [jobDescription, setJobDescription] = useState('');
    const result = useMemo(() => scoreResume(resumeData, jobDescription), [resumeData, jobDescription]);
    const colors = COLOR_MAP[result.grade.color];

    return (
        <div className="min-h-screen bg-slate-100">
            <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center gap-4">
                <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-lg">
                    <ArrowLeft size={20} />
                </button>
                <div className="flex items-center gap-2">
                    <Gauge className="text-blue-600" size={20} />
                    <h1 className="font-bold text-slate-800">ATS Score</h1>
                </div>
            </header>

            <div className="max-w-5xl mx-auto p-6 grid md:grid-cols-3 gap-6">
                {/* Score card */}
                <div className="md:col-span-1 space-y-4">
                    {/* Verdict banner — the opinionated "apply or skip" call */}
                    {result.verdict && (() => {
                        const verdictColors = COLOR_MAP[result.verdict.color];
                        const Icon = VERDICT_ICONS[result.verdict.action] || AlertTriangle;
                        return (
                            <div className={`rounded-xl border-2 ${verdictColors.border} ${verdictColors.soft} p-4`}>
                                <div className="flex items-center gap-2 mb-2">
                                    <Icon className={verdictColors.text} size={20} />
                                    <span className={`text-xs font-black uppercase tracking-wider ${verdictColors.text}`}>
                                        {result.verdict.action.replace('_', ' ')}
                                    </span>
                                </div>
                                <p className={`font-bold text-sm ${verdictColors.text} mb-1`}>{result.verdict.headline}</p>
                                <p className="text-xs text-slate-700 leading-relaxed">{result.verdict.reasoning}</p>
                            </div>
                        );
                    })()}

                    <div className={`bg-white rounded-xl shadow-sm border ${colors.border} p-6 text-center`}>
                        <div className={`inline-flex items-center justify-center w-32 h-32 rounded-full ${colors.bg} text-white text-5xl font-black mb-3`}>
                            {result.grade.letter}
                        </div>
                        <div className={`text-3xl font-bold ${colors.text}`}>{result.percentage}%</div>
                        <div className={`text-sm font-medium ${colors.text} mt-1`}>{result.grade.label}</div>
                        <div className="text-xs text-slate-500 mt-2">
                            {result.baseScore} / {result.baseMax} points
                        </div>
                    </div>

                    {/* JD input */}
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                        <label className="text-xs font-bold text-slate-600 uppercase mb-2 flex items-center gap-1">
                            <Target size={12} /> Job description (optional)
                        </label>
                        <textarea
                            value={jobDescription}
                            onChange={(e) => setJobDescription(e.target.value)}
                            placeholder="Paste a job description to get keyword-match analysis…"
                            rows={6}
                            className="w-full text-xs border border-slate-200 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                        />
                    </div>
                </div>

                {/* Checks list */}
                <div className="md:col-span-2 space-y-3">
                    {result.checks.map(c => {
                        const ratio = c.score / c.max;
                        const icon = ratio === 1
                            ? <CheckCircle2 className="text-emerald-500 shrink-0" size={18} />
                            : ratio >= 0.5
                                ? <AlertCircle className="text-amber-500 shrink-0" size={18} />
                                : <XCircle className="text-red-500 shrink-0" size={18} />;
                        return (
                            <div key={c.id} className="bg-white rounded-xl border border-slate-200 p-4">
                                <div className="flex items-start gap-3">
                                    {icon}
                                    <div className="flex-1">
                                        <div className="flex justify-between items-center mb-1">
                                            <h4 className="font-medium text-slate-800 text-sm">{c.label}</h4>
                                            <span className="text-xs font-mono text-slate-500">{c.score} / {c.max}</span>
                                        </div>
                                        <p className="text-xs text-slate-500 mb-2">{c.detail}</p>
                                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                            <div
                                                className={ratio === 1 ? 'bg-emerald-500 h-full' : ratio >= 0.5 ? 'bg-amber-500 h-full' : 'bg-red-500 h-full'}
                                                style={{ width: `${ratio * 100}%` }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {result.keywordCheck && (
                        <div className="bg-white rounded-xl border-2 border-blue-200 p-4">
                            <div className="flex justify-between items-center mb-2">
                                <h4 className="font-medium text-slate-800 text-sm flex items-center gap-2">
                                    <Target size={14} className="text-blue-600" /> {result.keywordCheck.label}
                                </h4>
                                <span className="font-bold text-blue-600">{result.keywordCheck.score}%</span>
                            </div>
                            <p className="text-xs text-slate-500 mb-3">{result.keywordCheck.detail}</p>
                            <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-4">
                                <div
                                    className="bg-blue-500 h-full transition-all"
                                    style={{ width: `${result.keywordCheck.score}%` }}
                                />
                            </div>
                            {result.keywordCheck.missing.length > 0 && (
                                <div>
                                    <p className="text-xs font-bold text-red-600 mb-1">Missing keywords (top 20):</p>
                                    <div className="flex flex-wrap gap-1">
                                        {result.keywordCheck.missing.map(k => (
                                            <span key={k} className="text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded-full">
                                                {k}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {result.keywordCheck.matched.length > 0 && (
                                <div className="mt-3">
                                    <p className="text-xs font-bold text-emerald-600 mb-1">Matched keywords (top 20):</p>
                                    <div className="flex flex-wrap gap-1">
                                        {result.keywordCheck.matched.map(k => (
                                            <span key={k} className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">
                                                {k}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ATSScorePanel;
